import Global from '../store/global-selectors';
import { selectOrderDetailsInventoryInfo } from '../store/orders/order-details/orders.details.selectors';
import { selectOrdersDrawerSingleOrderInventoryInfo } from '../store/orders/drawer/orders.drawer.selectors';
import { SellerProperties } from '../store/seller/seller-properties';
import { isShipFromExternal } from '../store/csw/csw.fulfillment-plan-to-csw';
import { FulfillmentPlanOrderGridStatus } from '../enums/fulfillmentplan-order-grid-status';

// This logic doesn't account for instances when two shipments contain the same quantity of the same product, think "split" shipments,
// where one of those shipments was allocated but the other wasn't.
const currentButFlawedIsItemAllocatedCheck = (allocationInfo, orderItem) => {
  const { allocatedQty } = allocationInfo.find((ai) => ai.sku === orderItem.sku) || {};
  return allocatedQty === orderItem.quantity;
};

class InventoryServiceClass {
  InventorySourceIdentifierType = {
    ShipFromId: 'ShipFromId',
    SellerFulfillmentProviderId: 'SellerFulfillmentProviderId',
  };

  StockLevelQuantityType = {
    InventoryProviderSpecified: 'InventoryProviderSpecified',
    BlankBox: 'BlankBox',
    Allocated: 'Allocated',
    Available: 'Available',
    Total: 'Total',
  };

  InventorySourceType = {
    Unknown: 'Unknown',
    None: 'None',
    ThirdParty: 'ThirdParty',
    Native: 'Native',
    Store: 'Store',
    FulfillmentProvider: 'FulfillmentProvider',
    Integrations: 'Integrations',
  };

  // does the allocate button appear
  isInventorySettingEnabled(state) {
    const { isInventoryEnabled } = new SellerProperties({ state });
    return isInventoryEnabled;
  }

  doesCurrentUserHaveInventoryRole(state) {
    return Global.selectUserhasInventoryManagementRole(state);
  }

  isFulfillmentPlanInventorySourceASellerFulfillmentProvider(fulfillmentPlan) {
    return (
      (!!fulfillmentPlan.shipFromId && isShipFromExternal(fulfillmentPlan.shipFromId)) ||
      (!fulfillmentPlan.shipFromId &&
        !!fulfillmentPlan.externalFulfillmentConfiguration &&
        !!fulfillmentPlan.externalFulfillmentConfiguration.sellerFulfillmentProviderId)
    );
  }

  isInventorySourceTypeThirdParty(inventorySourceType) {
    return (
      inventorySourceType === this.InventorySourceType.ThirdParty ||
      inventorySourceType === this.InventorySourceType.Store ||
      inventorySourceType === this.InventorySourceType.FulfillmentProvider ||
      inventorySourceType === this.InventorySourceType.Integrations
    );
  }

  doesSellerFulfillmentProviderSupportInventoryLevelRetrieval(state, sellerFulfillmentProviderId) {
    const sellerFulfillmentProviders = Global.selectSellerActiveFulfillmentProviders(state);
    const sellerFulfillmentProvider = sellerFulfillmentProviders.find(
      (sellerFufmintProvider) => sellerFufmintProvider.sellerFulfillmentProviderId === sellerFulfillmentProviderId,
    );
    if (!sellerFulfillmentProvider) {
      return false;
    }
    return sellerFulfillmentProvider.supportsInventoryLevelRetrieval;
  }

  doesShipFromHaveInventoryTrackingEnabled(shipFromId, state) {
    const shipFromInventorySourceType = Global.selectSellerShipFromInventorySourceType(state, { shipFromId });
    return shipFromId && (shipFromInventorySourceType === 'Native' || this.isInventorySourceTypeThirdParty(shipFromInventorySourceType));
  }

  doesShipFromHaveNativeInventoryTrackingEnabled(shipFromId, state) {
    const shipFromInventorySourceType = Global.selectSellerShipFromInventorySourceType(state, { shipFromId });
    return shipFromId && shipFromInventorySourceType === 'Native';
  }

  doesShipFromHaveThirdPartyInventoryTrackingEnabled(shipFromId, state) {
    const shipFromInventorySourceType = Global.selectSellerShipFromInventorySourceType(state, { shipFromId });
    return shipFromId && this.isInventorySourceTypeThirdParty(shipFromInventorySourceType);
  }

  doesFulfillmentPlanHaveActiveFulfillment(state, fulfillmentPlanId) {
    const fulfillments = Global.selectEntitiesFulfillmentsByFulfillmentPlanId(state, { fulfillmentPlanId });
    const activeFulfillment = fulfillments.find((fulfillment) => !fulfillment.isLabelVoided);
    return !!activeFulfillment;
  }

  canFulfillmentPlanBeAllocated(state, fulfillmentPlanId) {
    if (!fulfillmentPlanId) return false;
    if (!this.isInventorySettingEnabled(state)) return false;
    if (!this.doesCurrentUserHaveInventoryRole(state)) return false;

    const fulfillmentPlanShipFromId = Global.selectEntitiesFulfillmentPlanShipFromId(state, {
      fulfillmentPlanId,
    });
    if (!this.doesShipFromHaveNativeInventoryTrackingEnabled(fulfillmentPlanShipFromId, state)) return false;

    if (this.doesFulfillmentPlanHaveActiveFulfillment(state, fulfillmentPlanId)) return false;

    const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }) || {};
    const { orderGridStatus } = fulfillmentPlan;
    const isShipped = orderGridStatus === FulfillmentPlanOrderGridStatus.Shipped.name;
    const isCancelled = orderGridStatus === FulfillmentPlanOrderGridStatus.Cancelled.name;
    if (isShipped || isCancelled) {
      return false;
    }

    return true;
  }

  shouldItemInventoryInformationShow(state, fulfillmentPlanId) {
    const fulfillmentPlanShipFromId = Global.selectEntitiesFulfillmentPlanShipFromId(state, {
      fulfillmentPlanId,
    });
    const fulfillmentPlanSellerFulfillmentProviderId = Global.selectEntitiesFulfillmentPlanSellerFulfillmentProviderID(state, {
      fulfillmentPlanId,
    });

    if (fulfillmentPlanSellerFulfillmentProviderId) {
      return this.doesSellerFulfillmentProviderSupportInventoryLevelRetrieval(state, fulfillmentPlanSellerFulfillmentProviderId);
    }

    const shipFromHasInventoryTracking = this.doesShipFromHaveInventoryTrackingEnabled(fulfillmentPlanShipFromId, state);
    if (!shipFromHasInventoryTracking) {
      return false;
    }

    const isInventorySettingEnabled = this.isInventorySettingEnabled(state);
    if (!isInventorySettingEnabled && !this.doesShipFromHaveThirdPartyInventoryTrackingEnabled(fulfillmentPlanShipFromId, state)) {
      return false;
    }

    if (this.doesFulfillmentPlanHaveActiveFulfillment(state, fulfillmentPlanId)) {
      return false;
    }
    const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }) || {};
    const { orderGridStatus } = fulfillmentPlan;
    const isShipped = orderGridStatus === FulfillmentPlanOrderGridStatus.Shipped.name;
    const isCancelled = orderGridStatus === FulfillmentPlanOrderGridStatus.Cancelled.name;
    if (isShipped || isCancelled) {
      return false;
    }

    return true;
  }

  getItemStockLevelInfo(state, isInOrdersDrawer, fulfillmentPlanId, orderItem) {
    const inventoryInfo = isInOrdersDrawer ? selectOrdersDrawerSingleOrderInventoryInfo(state) : selectOrderDetailsInventoryInfo(state);
    if (!inventoryInfo) return undefined;

    const { stockLevelInfo = [] } = inventoryInfo;
    if (!stockLevelInfo.length) return undefined;

    const selectedShipFromId = Global.selectEntitiesFulfillmentPlanShipFromId(state, { fulfillmentPlanId });
    const selectedFulfillmentProviderId = Global.selectEntitiesFulfillmentPlanSellerFulfillmentProviderID(state, {
      fulfillmentPlanId,
    });
    const selectedInventorySourceId = Global.selectEntitiesFulfillmentPlanIsExternal(state, {
      fulfillmentPlanId,
    })
      ? selectedFulfillmentProviderId
      : selectedShipFromId;
    return stockLevelInfo.find((sli) => {
      return (
        `${sli.inventorySourceId}` === selectedInventorySourceId &&
        (`${sli.fulfillmentPlanItemId}` === orderItem.fulfillmentPlanItemId || sli.sku === orderItem.sku)
      );
    });
  }

  getItemAvailableQuantity(state, isInOrdersDrawer, fulfillmentPlanId, orderItem) {
    const thisItemsStockLevelInfo = this.getItemStockLevelInfo(state, isInOrdersDrawer, fulfillmentPlanId, orderItem);
    if (!thisItemsStockLevelInfo || (thisItemsStockLevelInfo && thisItemsStockLevelInfo.isUntracked)) {
      return;
    }

    const { stockLevels = [] } = thisItemsStockLevelInfo;
    if (!stockLevels.length) {
      return;
    }

    const { quantity } = stockLevels.find((sl) => sl.type === this.StockLevelQuantityType.Available) || {};
    return quantity;
  }

  getItemTotalQuantity(state, isInOrdersDrawer, fulfillmentPlanId, orderItem) {
    const thisItemsStockLevelInfo = this.getItemStockLevelInfo(state, isInOrdersDrawer, fulfillmentPlanId, orderItem) || {};

    const { stockLevels = [] } = thisItemsStockLevelInfo;
    if (!stockLevels.length) return undefined;

    const { quantity } = stockLevels.find((sl) => sl.type === this.StockLevelQuantityType.Total) || {};
    return quantity;
  }

  canItemBeAllocated(state, isInOrdersDrawer, fulfillmentPlanId, orderItem) {
    const availableQty = this.getItemAvailableQuantity(state, isInOrdersDrawer, fulfillmentPlanId, orderItem);
    return typeof availableQty === 'number' ? availableQty >= orderItem.quantity : false;
  }

  isItemAllocated(state, isInOrdersDrawer, fulfillmentPlanId, orderItem) {
    const inventoryInfo = isInOrdersDrawer ? selectOrdersDrawerSingleOrderInventoryInfo(state) : selectOrderDetailsInventoryInfo(state);
    if (!inventoryInfo) return false;

    const { allocationInfo = [] } = inventoryInfo;
    // we dont support partial allocation so consumer only needs to know true / false
    return currentButFlawedIsItemAllocatedCheck(allocationInfo, orderItem);
  }

  isItemInventoryUnknown(state, isInOrdersDrawer, fulfillmentPlanId, orderItem) {
    const thisItemsStockLevelInfo = this.getItemStockLevelInfo(state, isInOrdersDrawer, fulfillmentPlanId, orderItem);

    if (thisItemsStockLevelInfo && thisItemsStockLevelInfo.stockLevels && thisItemsStockLevelInfo.stockLevels.length) return false;

    return true;
  }

  setupInventoryInfo(inventoryInfo, state) {
    // calculate aggregate allocation information
    const nativeInventoryInfo = inventoryInfo.stockLevelInfo
      .filter((sli) => sli.providerName === 'ShipStation')
      .map((csli) => ({
        sku: csli.sku,
        allocatedQty: csli.stockLevels.find((sl) => sl.type === this.StockLevelQuantityType.Allocated)?.quantity || 0,
      }));
    const aggregatedAllocationInfo = [
      ...nativeInventoryInfo
        .reduce((map, item) => {
          const key = item.sku;
          const { allocatedQty } = item;
          const prev = map.get(key);

          if (prev) {
            prev.allocatedQty += allocatedQty;
          } else {
            map.set(key, Object.assign({}, { ...item, allocatedQty }));
          }

          return map;
        }, new Map())
        .values(),
    ];

    return {
      ...state,
      inventoryInfo: {
        isLoading: false,
        stockLevelInfo: inventoryInfo.stockLevelInfo,
        allocationInfo: aggregatedAllocationInfo,
      },
    };
  }
}
export const InventoryService = new InventoryServiceClass();
